import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  QueryObserverOptions,
  UseQueryOptions,
} from 'react-query'
import { SessionsApi } from '@trybeapp/sdk.shop'
import {
  CopySessionTypeRequest,
  ListSessionTypes,
  ListSessionTypesRequest,
  SessionType,
  SessionTypesApi as SessionTypesApiTs,
  SessionTypeResponse,
  SessionRecurrenceGroup,
  ResponseError,
} from '@trybeapp/sdk'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { usePageQueryParam } from 'hooks/UsePageQueryParam'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import { minutesToMilliseconds, sdkDateToSystemDateTime, toIsoDate } from 'utilities/DateUtils'
import { useOfferingSearch } from 'api/OfferingSearch'
import { TYPE_SESSION } from 'constants/offeringTypes'
import type { ErrorNotificationProps } from 'ui/components/Notification'

const api = new SessionsApi()
const tsApi = new SessionTypesApiTs(apiConfiguration)

export const useSessionTypes = (
  params: ListSessionTypesRequest = {},
  options: UseQueryOptions<ListSessionTypes> = {}
) => {
  const siteId = useCurrentSiteId()

  params = {
    siteId,
    includeRecurrenceGroups: false,
    ...params,
  }

  return useQuery<ListSessionTypes, ResponseError>(
    ['sessionTypes', params],
    async () => tsApi.listSessionTypes(params),
    options
  )
}

export const useAllSessionTypes = (options: UseQueryOptions = {}) => {
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? Infinity

  return useOfferingSearch(
    {
      perPage: 1000,
      offeringType: [TYPE_SESSION],
    },
    options
  )
}

export const useSessionType = (
  sessionTypeId: string,
  options: UseQueryOptions<SessionTypeResponse> = {}
) =>
  useQuery<SessionTypeResponse, ResponseError>(
    ['sessionType', sessionTypeId],
    async () => {
      const response = await tsApi.getSessionType({ sessionTypeId })
      const recurrenceGroups = response.data?.recurrence_groups ?? []

      recurrenceGroups.forEach((recurrenceGroup) => {
        if (recurrenceGroup.recurrence_start) {
          recurrenceGroup.recurrence_start = sdkDateToSystemDateTime(
            recurrenceGroup.recurrence_start
          ).toJSDate()
        }

        if (recurrenceGroup.recurrence_end) {
          recurrenceGroup.recurrence_end = sdkDateToSystemDateTime(
            recurrenceGroup.recurrence_end
          ).toJSDate()
        }
      })

      return response
    },
    options
  )

export type CopySessionTypeOptions = Pick<
  CopySessionTypeRequest,
  'includePriceRules' | 'includeRecurrenceGroups'
>

interface CopySessionTypeParams {
  options?: CopySessionTypeOptions
  sessionType: SessionType
}

export const useCopySessionType = () => {
  const queryClient = useQueryClient()

  return useMutation<
    SessionTypeResponse,
    NonNullable<ErrorNotificationProps['error']>,
    CopySessionTypeParams
  >(
    ({ sessionType, options = {} }) =>
      tsApi.copySessionType({
        ...options,
        sessionTypeId: sessionType.id,
        sessionType: sessionType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessionTypes'])
      },
    }
  )
}

export const useCreateSessionType = () => {
  const queryClient = useQueryClient()

  return useMutation<
    { data: SessionType },
    NonNullable<ErrorNotificationProps['error']>,
    Record<string, unknown>
  >((sessionType) => api.sessionTypeStore(sessionType), {
    onSuccess: () => {
      queryClient.invalidateQueries(['sessionTypes'])
    },
  })
}

export const useUpdateSessionType = () => {
  const queryClient = useQueryClient()

  return useMutation<
    { data: SessionType },
    NonNullable<ErrorNotificationProps['error']>,
    [SessionType['id'], Record<string, unknown>]
  >(async ([sessionTypeId, vals]) => api.sessionTypeUpdate(sessionTypeId, vals), {
    onSuccess: () => {
      queryClient.invalidateQueries('sessionType')
    },
  })
}

export const useDeleteSessionType = () => {
  const queryClient = useQueryClient()

  return useMutation((sessionTypeId: SessionType['id']) => api.sessionTypeDestroy(sessionTypeId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['sessionTypes'])
    },
  })
}

export const useRestoreSessionType = () => {
  const queryClient = useQueryClient()

  return useMutation((sessionTypeId: SessionType['id']) => api.sessionTypeRestore(sessionTypeId), {
    onSuccess: () => {
      queryClient.invalidateQueries('sessionTypes')
    },
  })
}

export const useInfiniteSessionTypes = (
  params: Omit<ListSessionTypesRequest, 'siteId'>,
  options?: {}
) => {
  const siteId = useCurrentSiteId()

  return useInfiniteQuery(['sessionTypes', { ...params, siteId }], fetchSessionTypes, {
    getNextPageParam: (lastPage, pages) => {
      return lastPage.nextPage
    },
    ...options,
  })
}

const fetchSessionTypes = async ({ queryKey, pageParam: page = 1 }) => {
  const { siteId, ...params } = queryKey[1]

  const response = await tsApi.listSessionTypes({ page, siteId, ...params })

  return {
    data: response.data,
    page,
    nextPage: response.meta.last_page > page ? page + 1 : undefined,
    total: response.meta.total,
  }
}

/**
 * Recurrence Groups.
 */

export const useCheckUpdateRecurrenceGroup = () => {
  return useMutation<
    { data: SessionRecurrenceGroup },
    NonNullable<ErrorNotificationProps['error']>,
    [
      sessionTypeId: SessionType['id'],
      recurrenceGroupId: SessionRecurrenceGroup['id'],
      recurrenceGroup: Record<string, unknown>,
    ]
  >(async ([sessionTypeId, recurrenceGroupId, recurrenceGroup]) => {
    return await api.sessionRecurrenceGroupCheckUpdate(
      sessionTypeId,
      recurrenceGroupId,
      formatRequestBody(recurrenceGroup)
    )
  })
}

export function useCheckUpdateRecurrenceGroupCancellations(
  sessionTypeId: string,
  recurrenceGroupId: string,
  recurrenceGroup: Record<string, unknown>,
  page: number,
  options: QueryObserverOptions = {}
) {
  return useQuery(
    ['sessionType', { sessionTypeId, recurrenceGroupId, recurrenceGroup, page }],
    () => {
      const body = { ...formatRequestBody(recurrenceGroup), page }

      return api.sessionRecurrenceGroupCheckUpdateCancellations(
        sessionTypeId,
        recurrenceGroupId,
        body
      )
    },
    { staleTime: minutesToMilliseconds(1), ...options }
  )
}

const formatRequestBody = (requestBody) => {
  const updatedBody = { ...requestBody }

  if (updatedBody.recurrence_start) {
    updatedBody.recurrence_start = toIsoDate(updatedBody.recurrence_start)
  }

  if (updatedBody.recurrence_end) {
    updatedBody.recurrence_end = toIsoDate(updatedBody.recurrence_end)
  }

  return updatedBody
}

/**
 * Price rules.
 */

export const useSessionTypePriceRules = (
  sessionTypeId: string,
  params: any = {},
  options: UseQueryOptions = {}
) =>
  useQuery<any, any, any>(
    ['sessionTypePriceRules', sessionTypeId, { params }],
    async () => {
      const response = await tsApi.listSessionTypePriceRules({ sessionTypeId, ...params })

      response.data.forEach((priceRule) => {
        priceRule.date_from = priceRule.date_from
          ? sdkDateToSystemDateTime(priceRule.date_from).toJSDate()
          : null

        priceRule.date_to = priceRule.date_to
          ? sdkDateToSystemDateTime(priceRule.date_to).toJSDate()
          : null
      })

      return response
    },
    options
  )

export const usePagedSessionTypePriceRulesIndex = (
  sessionTypeId: string,
  params = {},
  options: UseQueryOptions = {}
) => {
  const [page] = usePageQueryParam()

  return useSessionTypePriceRules(sessionTypeId, { page, params }, options)
}

export const useCreateSessionTypePriceRule = (sessionTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (values: any) =>
      api.sessionTypePriceRuleStore(sessionTypeId, {
        weekdays: values.weekdays,
        date_from: toIsoDate(values.date_from),
        date_to: toIsoDate(values.date_to),
        time_from: values.time_from,
        time_to: values.time_to,
        price: values.price,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessionTypePriceRules', sessionTypeId])
        queryClient.invalidateQueries(['sessionTypes', sessionTypeId])
      },
    }
  )
}

export const useUpdateSessionTypePriceRule = (sessionTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    ([ruleId, values]: any) =>
      api.sessionTypePriceRuleUpdate(sessionTypeId, ruleId, {
        weekdays: values.weekdays,
        date_from: toIsoDate(values.date_from),
        date_to: toIsoDate(values.date_to),
        time_from: values.time_from,
        time_to: values.time_to,
        price: values.price,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessionTypePriceRules', sessionTypeId])
        queryClient.invalidateQueries(['sessionTypes', sessionTypeId])
      },
    }
  )
}

export const useDeleteSessionTypePriceRule = (sessionTypeId: string) => {
  const queryClient = useQueryClient()

  return useMutation((ruleId: any) => api.sessionTypePriceRuleDestroy(sessionTypeId, ruleId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['sessionTypePriceRules', sessionTypeId])
      queryClient.invalidateQueries(['sessionTypes', sessionTypeId])
    },
  })
}

export const useSuggestDescriptionForSessionType = (sessionTypeId: string) =>
  useMutation((description?: string) =>
    tsApi.actionSuggestDescriptionForSessionType({
      sessionTypeId,
      suggestDescription: { description },
    })
  )
